import type { LineLabelConfig, AngleLabelConfig } from '@examdojo/core/widget';
import type { ThemeColorName } from '@examdojo/styles';
import { deepMerge } from '@examdojo/core/widget';
import type { GeometryElementDisplayConfig } from '../../geometrypad.model';

/**
 * Resolve a style for a specific subpart of a geometry element.
 *
 * Returns the root-level style deep-merged with the subpart override.
 * If subpartName is undefined or no subpart override exists, returns the root style.
 *
 * Cascade: subpart override → element-level → (caller provides creator default)
 *
 * @param elementDisplay - The per-element display config (may be undefined)
 * @param styleKey - Which style bucket to resolve ('line', 'point', 'angle', etc.)
 * @param subpartName - The subpart name matching port suffix (e.g., 'AB', 'A', 'bottom')
 */
export function resolveSubpartStyle<K extends 'point' | 'line' | 'polygon' | 'angle' | 'circle'>(
  elementDisplay: GeometryElementDisplayConfig | undefined,
  styleKey: K,
  subpartName: string | undefined,
): GeometryElementDisplayConfig[K] | undefined {
  const rootStyle = elementDisplay?.[styleKey];
  if (!subpartName) {
    return rootStyle;
  }

  const subpartStyle = elementDisplay?.subparts?.[subpartName]?.[styleKey];
  if (!subpartStyle) {
    return rootStyle;
  }
  if (!rootStyle) {
    return subpartStyle;
  }

  return deepMerge(rootStyle, subpartStyle) as GeometryElementDisplayConfig[K];
}

/**
 * Resolve edge label with per-subpart override support.
 *
 * - `override === undefined`: use the globally-computed label (current behavior)
 * - `override === false` or `{ visible: false }`: no label (explicit hide)
 * - `override === true` or `{ visible: true, ... }`: show label; use global label if available,
 *   otherwise create a basic text-only label
 */
export function resolveSubpartLabelOverride(
  override: LineLabelConfig | boolean | undefined,
  globalLabel: LineLabelConfig | false,
  fallbackText: string,
  fallbackColor: ThemeColorName,
): LineLabelConfig | false {
  if (override === undefined) {
    return globalLabel;
  }
  if (override === false) {
    return false;
  }
  if (typeof override === 'object' && override.visible === false) {
    return false;
  }

  // Explicit show: determine base, then spread override properties on top
  const base: LineLabelConfig = globalLabel || {
    visible: true,
    variant: 'secondary',
    color: fallbackColor,
    text: fallbackText,
  };
  if (override === true) {
    return base;
  }
  return { ...base, ...override };
}

/**
 * Resolve angle label with per-subpart override support.
 * Same logic as edge labels but for angle label configs.
 */
export function resolveAngleLabelOverride(
  override: AngleLabelConfig | boolean | undefined,
  globalLabel: AngleLabelConfig | false,
  fallbackName: string,
  fallbackColor: ThemeColorName,
): AngleLabelConfig | false {
  if (override === undefined) {
    return globalLabel;
  }
  if (override === false) {
    return false;
  }
  if (typeof override === 'object' && override.visible === false) {
    return false;
  }

  const base: AngleLabelConfig = globalLabel || {
    visible: true,
    variant: 'secondary',
    color: fallbackColor,
    text: fallbackName,
    placement: 'outside',
  };
  if (override === true) {
    return base;
  }
  return { ...base, ...override };
}
